<template>
  <onboard-wrap>
    <template slot="content">
      <h1 class="h3">Great! Let's get acquainted.</h1>
      <p>Tell us a little about your organization. We'll use this to make your experience even better.</p>
      <div class="form-area dynamic">
        <div class="form-row">
          <p ref="name-error" class="field-error" :class="{ 'show-error' : nameError }">{{nameError}}</p>
          <div class="input-wrap" :class="{ 'show-error' : nameError }">
            <input v-model="teamName" name="teamName" placeholder="" type="text" :maxlength="teamNameChars.max" :class="{'active': (activeField.teamName || teamName)}" @focus="activeField.teamName = true">
            <label for="teamName">Organization name</label>
          </div>
        </div>
        <div class="form-row">
          <div class="industry-box">
            <p>Industry</p>
            <div class="radio-wrap">
              <div class="industry" v-for="industry in industries" :value="industry.value" v-bind:key="industry.value">
                <label :for="industry.id" :class="{'selected': userIndustry === industry.value}" @click="inputIndustry = ''">{{industry.value}}</label>
                <input v-model="userIndustry" :id="industry.id" type="radio" :value="industry.value">
              </div>
            </div>
            <input v-if="userIndustry === 'Other'"
              v-model="inputIndustry"
              name="inputIndustry"
              role="combobox"
              class="no-box"
              autocomplete="off"
              aria-owns="results"
              placeholder="Please specify"
              type="text"
              maxlength="30"
              @keyup="typing($event)"
              @keydown="autofill($event)">
            <ul v-if="autofillResults.length > 0" id="results" role="listbox" ref="autofillResults" class="autofill-results">
              <li v-for="(result, i) in autofillResults"
                :key="i"
                role="option"
                :query="result"
                :aria-selected="result === inputIndustry"
                :class="{'current': result === inputIndustry}"
                class="autocomplete-result"
                @click="selectIndustry(result)">{{result}}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <p v-if="errorMessage" class="alert alert-danger">{{errorMessage}}</p>
      <submit-button :finished="finished" @submit="submit">Continue to final step</submit-button>
    </template>
    <template slot="extra">
      <img class="mobile-img" v-if="is.mobile()" src="./images/mobile@2x.png" alt="">
      <app-demo v-else></app-demo>
      <div class="text-center">
        <h2>So easy, a bear could use&nbsp;it </h2>
        <p>Simple to set up. Delightful to use. TunnelBear keeps your team secure&#8212; without getting in their way.</p>
      </div>
      <div class="devices text-center">
        <img src="@/images/apps-list.svg" alt="Windows, macOS, Android, and iOS">
        <p class="fine-print-lg">TunnelBear is available for macOS, Windows, Android & iOS</p>
      </div>
    </template>
  </onboard-wrap>
</template>

<script>
import onboardWrap from '@/components/onboard-wrap/onboard-wrap'
import appDemo from '@/components/app-demo/app-demo'
import submitButton from '@/components/submit-button/submit-button'
import industryList from './data/li-industry-names.json'
import is from 'is_js'

export default {
  name: 'name',
  components: {
    'onboard-wrap': onboardWrap,
    'app-demo': appDemo,
    'submit-button': submitButton
  },
  data () {
    return {
      is: is,
      teamName: '',
      userIndustry: '',
      inputIndustry: '',
      industries: [
        {id: 'software', value: 'Computer Software'},
        {id: 'it', value: 'Information Technology & Services'},
        {id: 'healthcare', value: 'Hospital & Health Care'},
        {id: 'financial', value: 'Financial Services'},
        {id: 'marketing', value: 'Marketing & Advertising'},
        {id: 'consulting', value: 'Management Consulting'},
        {id: 'capital', value: 'Venture Capital & Private Equity'},
        {id: 'other', value: 'Other'}
      ],
      inputMemory: '',
      autofillResults: [],
      listIndex: -1,
      activeField: {
        teamName: false
      },
      nameError: '',
      errorMessage: '',
      finished: false,
      industryList: industryList,
      teamNameChars: {
        max: 40,
        min: 6
      }
    }
  },
  methods: {
    selectIndustry: function (selected) {
      this.inputIndustry = selected
      this.autofillResults = []
    },
    filterResults: function () {
      if (this.inputIndustry.length > 2) {
        let industrySearch = this.inputIndustry.toLowerCase()
        var results = this.industryList.filter((item) => {
          let currentItem = item.toLowerCase()
          return currentItem.indexOf(industrySearch) > -1 && currentItem !== industrySearch
        })
      }
      this.autofillResults = results || []
    },
    typing: function (event) {
      if (['ArrowDown', 'ArrowUp', 'Escape'].indexOf(event.key) === -1) {
        this.filterResults()
        this.inputMemory = this.inputIndustry
      }
    },
    autofill: function (event) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()
        this.scrollList(event.key)
        return
      } else if (event.key === 'Escape') {
        this.inputIndustry = this.inputMemory
        this.autofillResults = []
      }
      this.listIndex = -1
    },
    scrollList: function (key) {
      let inputFieldIndex = -1
      let maxIndex = this.autofillResults.length - 1
      let edgeIndex = {
        ArrowDown: maxIndex,
        ArrowUp: 0
      }
      let nextIndex = {
        ArrowDown: (this.listIndex === maxIndex ? inputFieldIndex : this.listIndex + 1),
        ArrowUp: (this.listIndex === inputFieldIndex ? maxIndex : this.listIndex - 1)
      }
      if (this.listIndex === edgeIndex[key]) {
        this.listIndex = nextIndex[key]
        this.inputIndustry = this.inputMemory
      } else {
        this.listIndex = nextIndex[key]
        this.inputIndustry = this.autofillResults[this.listIndex]
      }
    },
    submit: function () {
      let industry = this.inputIndustry || this.userIndustry || ''
      let teamData = {
        teamName: this.teamName,
        industry: industry.replace('&', 'and')
      }

      this.errorMessage = ''
      if (!this.teamName || this.teamName.replace(/\s+/g, ' ').trim().length < this.teamNameChars.min) {
        this.nameError = `Grr... your organization name must be a minimum of ${this.teamNameChars.min} characters.`
        return
      } else if (this.teamName.length > this.teamNameChars.max) {
        this.nameError = `Grr... your organization name can't be longer than ${this.teamNameChars.max} characters.`
        return
      }
      this.$store.dispatch('submitting', true)

      this.$http.get(this.ENV.coreBaseUrl + '/team/validateName', {
        params: {
          name: this.teamName
        }
      }).then((response) => {
        this.$store.dispatch('submitting', false)
        if (response.data.result === 'FAIL') {
          if (response.data.details) {
            this.errorMessage = response.data.details
          } else {
            this.errorMessage = 'There is an issue with this organization name. Please choose another name.'
          }
          this.$store.dispatch('submitting', false)
        } else {
          this.$store.dispatch('newTeamDetails', teamData)
          this.finished = true
          setTimeout(() => {
            this.$store.dispatch('submitting', false)
            this.$router.push({name: 'pay'})
          }, 1000)
        }
      }).catch(() => {
        this.errorMessage = 'Grrr... something went wrong with your request, please try again'
        this.$store.dispatch('submitting', false)
      })
    }
  },
  watch: {
    teamName: function () {
      if (this.teamName.replace(/\s+/g, ' ').trim().length >= this.teamNameChars.min && name.length <= this.teamNameChars.max) {
        this.nameError = ''
      }
    }
  }
}
</script>

<style scoped lang="stylus" src="./name.styl" >



// WEBPACK FOOTER //
// src/views/onboarding/views/name/name.vue